import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { useData } from "../../context/DataContext";
import { useAuth } from "../../context/AuthContext";
import { projectRawTotal, projectWeightedTotal } from "../../lib/calc";
import { fmtMoney, fmtDate, contractLabel, projectNumber } from "../../lib/format";
import { currentInvoicePeriod } from "../../lib/invoicing";
import InfoTip from "../../components/InfoTip";
import { toast } from "sonner";
import { Plus, Pencil, AlertCircle, CheckCircle2, ChevronUp, ChevronDown, ChevronRight } from "lucide-react";
import { usePrefs, applyOrder, moveInList } from "../../lib/prefs";
import { isCompleted, isPastEndDate } from "../../lib/status";
import { altProjectTotal, fmtAltValue, fxAsAt, fxRound, fxRuleCaption, hasAltCurrency, liveRateOf } from "../../lib/currency";
import { useFx } from "../../lib/useFx";
import PageHeader from "../../components/PageHeader";
import DeleteProjectButton from "../../components/DeleteProjectButton";

// Lifts the email local-part (e.g. "ella.lilley") out of a full email so the
// "last edited by" badge stays compact in narrow table cells.
function emailHandle(email) {
  if (!email || typeof email !== "string") return "";
  return email.split("@")[0];
}

export default function MyPortfolio() {
  const { data, rawData, confirmInvoicing, viewAs, refresh } = useData();
  const { user } = useAuth();
  const loc = useLocation();
  const [confirming, setConfirming] = useState(false);
  const [showCompleted, setShowCompleted] = useState(false);
  const { prefs, savePref } = usePrefs();
  // Live ECB rate for the forecast side of any non-NZD project.
  const fx = useFx();
  // The same page is mounted under two routes: /portfolio for principals and
  // /admin/my-projects for admins. Detect which we're in so all internal
  // links land in the right place.
  const inAdmin = loc.pathname.startsWith("/admin/");
  const base = inAdmin ? "/admin/my-projects" : "/portfolio";

  // Leave editor: firm-wide project view grouped by principal. No filters
  // (she sees every project) and no invoicing/add-project affordances.
  if (user?.role === "leave_editor") {
    return <ByPrincipalPortfolio data={data} rawData={rawData} base={base} />;
  }

  // Whose portfolio is this? An admin in view-as mode acts for the viewed
  // principal (dataset is already server-scoped to them); otherwise the
  // caller's own linked principal. Principals receive a server-scoped
  // dataset, so their filter is a no-op.
  const actingAsViewed = user?.role === "admin" && !!viewAs;
  const myPrincipalId = actingAsViewed ? viewAs : (user?.principalId || null);
  const projectsUnordered = (data?.projects ?? []).filter(
    (p) => !myPrincipalId || p.principalId === myPrincipalId
  );
  // Per-user row order (saved server-side, so it follows the user).
  const projects = applyOrder(projectsUnordered, prefs?.projectOrder);
  const moveProject = (index, delta) => {
    const next = moveInList(projects.map((p) => p.id), index, delta);
    if (next) savePref("projectOrder", next);
  };
  // Wayfinding: name the owning principal so the PageHeader reads
  // "PORTFOLIO · ELLA LILLEY-GASTEIGER" whether an admin is viewing-as OR a
  // real principal is logged in. Associates have no linked principal, so
  // their header falls back to a generic label.
  const principalName = myPrincipalId
    ? rawData?.principals?.find((pr) => pr.id === myPrincipalId)?.name || null
    : null;

  // Invoicing confirmation for the current calendar month.
  const period = currentInvoicePeriod();
  const invoiceStamp = myPrincipalId
    ? rawData?.invoicing?.[myPrincipalId]?.[period.key] || null
    : null;

  const handleConfirmInvoicing = async () => {    setConfirming(true);
    try {
      await confirmInvoicing();
      toast.success(`Invoicing confirmed for ${period.label}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm invoicing");
    } finally {
      setConfirming(false);
    }
  };

  // Live vs Completed — a DISPLAY split only. Completed projects stay in the
  // dataset and therefore in every total, chart and history calculation.
  const liveProjects = projects.filter((p) => !isCompleted(p));
  const completedProjects = projects.filter((p) => isCompleted(p));

  const renderRow = (p, rowIndex, done = false) => {
    const chip = p.contract === "Yes" ? "chip-yes" : p.contract === "Tentative" ? "chip-tentative" : "chip-no";
    return (
      <tr key={p.id} className={`border-t border-[var(--rule)] hover:bg-[var(--paper-soft)] ${done ? "bg-[var(--paper-soft)]/40 text-[var(--muted-fg)]" : ""}`} data-testid={`portfolio-row-${p.id}`}>
        {/* Row order — per-user, saved server-side */}
        <td className="px-1 py-2 align-middle">
          <div className="flex flex-col items-center">
            <button onClick={() => moveProject(rowIndex, -1)} disabled={rowIndex === 0}
              className="px-2 text-[var(--soft-fg)] hover:text-[var(--ink)] disabled:opacity-25"
              title="Move up" aria-label={`Move ${p.name} up`} data-testid={`move-up-${p.id}`}>
              <ChevronUp size={14} />
            </button>
            <button onClick={() => moveProject(rowIndex, 1)} disabled={rowIndex === projects.length - 1}
              className="px-2 text-[var(--soft-fg)] hover:text-[var(--ink)] disabled:opacity-25"
              title="Move down" aria-label={`Move ${p.name} down`} data-testid={`move-down-${p.id}`}>
              <ChevronDown size={14} />
            </button>
          </div>
        </td>
        {/* Delete lives at the FAR LEFT of the row — the opposite end from the
            edit pen — so the two can never be confused. */}
        {user?.role === "admin" && (
          <td className="px-2 py-3 text-center border-r border-[var(--rule)]">
            <DeleteProjectButton project={p} isAdmin onDeleted={refresh} />
          </td>
        )}
        <td className="px-4 py-3">
          <div className="flex items-center gap-2">
            {p.riskFlag === "attention" && (
              <span className="inline-flex items-center" title="Risk flag — needs attention">
                <AlertCircle size={14} className="text-[#6B2F2A]" />
                <InfoTip side="right" label="Risk flag">
                  You’ve flagged this project as <strong>needs principal attention</strong>. Visible to admins on the firm-wide view.
                </InfoTip>
              </span>
            )}
            <div>
              <div className={done ? "" : "text-[var(--ink)]"}>{p.name}</div>
              <div className="font-mono-num text-[10px] text-[var(--soft-fg)]">{projectNumber(p)} {p.client ? `· ${p.client}` : ""}</div>
              {isPastEndDate(p) && (
                <div className="text-[10px] text-[#8a6d1a] mt-0.5" data-testid={`past-end-${p.id}`}>
                  Past expected end date — still live
                </div>
              )}
              {p.lastEditedAt && (
                <div className="text-[10px] text-[var(--soft-fg)] mt-0.5" data-testid={`last-edited-${p.id}`}>
                  Edited {fmtDate(p.lastEditedAt)}{p.lastEditedBy ? ` · ${emailHandle(p.lastEditedBy)}` : ""}
                </div>
              )}
            </div>
          </div>
        </td>
        <td className="px-4 py-3 font-mono-num text-xs">{p.status || "In Progress"}</td>
        <td className="px-4 py-3"><span className={`chip ${chip}`}>{contractLabel(p.contract)}</span></td>
        <td className="px-4 py-3 font-mono-num text-xs" data-testid={`currency-${p.id}`}>
          {p.currency && p.currency !== "NZD" ? (
            <div>
              <span title={p.fxRate ? `Locked rate: 1 NZD = ${fxRound(p.fxRate)} ${p.currency}` : "No rate set yet"}>
                {p.currency}{p.fxRate ? "" : " ·no rate"}
              </span>
              {liveRateOf(p, fx) > 0 && (
                <div className="text-[10px] text-[var(--soft-fg)]" data-testid={`fx-as-at-${p.id}`}>
                  live {fxRound(liveRateOf(p, fx))} · ECB {fxAsAt(fx)}
                </div>
              )}
            </div>
          ) : (
            <span className="text-[var(--rule-strong)]">NZD</span>
          )}
        </td>
        <td className="px-4 py-3 text-right font-mono-num">
          {fmtMoney(projectRawTotal(p))}
          {hasAltCurrency(p) && (
            <div className="text-[10px] text-[var(--soft-fg)]" data-testid={`raw-alt-${p.id}`} title={fxRuleCaption(p, fx) || undefined}>
              {fmtAltValue(altProjectTotal(p, fx), p.currency)}
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-right font-mono-num text-[var(--soft-fg)]">
          {fmtMoney(projectWeightedTotal(p))}
          {hasAltCurrency(p) && (
            <div className="text-[10px]" data-testid={`weighted-alt-${p.id}`} title={fxRuleCaption(p, fx) || undefined}>
              {fmtAltValue(altProjectTotal(p, fx, { weighted: true }), p.currency)}
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-right">
          <Link to={`${base}/edit/${encodeURIComponent(p.id)}`}
            className="inline-flex items-center justify-center h-11 w-11 text-[var(--soft-fg)] hover:text-[var(--ink)] hover:bg-[var(--paper-soft)] transition-colors"
            title="Edit project"
            aria-label={`Edit project ${p.name}`}
            data-testid={`edit-${p.id}`}>
            <Pencil size={15} />
          </Link>
        </td>
      </tr>
    );
  };

  return (
    <div className="space-y-8" data-testid="my-portfolio">
      <PageHeader
        section={principalName ? `PORTFOLIO · ${principalName.toUpperCase()}` : (user?.role === "associate" ? "PORTFOLIO · ASSOCIATE" : "MY PROJECTS")}
        title={principalName ? `${principalName}'s projects` : "My projects"}
        subtitle={`${liveProjects.length} live${completedProjects.length ? ` · ${completedProjects.length} completed` : ""} — click any row to open the project form. Completed work stays in every total, chart and history.`}
        right={
          <div className="flex items-center gap-3">
            {myPrincipalId && (
              invoiceStamp ? (
                <span
                  className="pill pill-yes"
                  data-testid="invoicing-confirmed-badge"
                  title={`Confirmed ${fmtDate(invoiceStamp.confirmedAt)} by ${invoiceStamp.confirmedBy}`}
                >
                  <CheckCircle2 size={11} /> Invoicing confirmed · {period.label}
                </span>
              ) : (
                <button
                  onClick={handleConfirmInvoicing}
                  disabled={confirming}
                  className="pill pill-info hover:bg-[var(--paper-soft)] cursor-pointer disabled:opacity-50"
                  data-testid="confirm-invoicing-btn"
                >
                  <CheckCircle2 size={11} /> Confirm invoicing · {period.label}
                </button>
              )
            )}
            <Link to={`${base}/add`} className="inline-flex items-center gap-2 px-3 py-2 bg-[var(--ink)] text-white text-xs font-display tracked-tight uppercase hover:opacity-90 transition-opacity" data-testid="add-project-link">
              <Plus size={12} /> Add Project
            </Link>
          </div>
        }
      />

      <div className="border border-[var(--rule)]">
        <table className="w-full text-sm">
          <thead>
            <tr className="eyebrow text-left">
              <th className="w-10 px-1 py-3"></th>
              {user?.role === "admin" && <th className="w-16 px-2 py-3 border-r border-[var(--rule)]"></th>}
              <th className="px-4 py-3">Project</th>
              <th className="px-4 py-3">
                <span className="inline-flex items-center">
                  Status
                  <InfoTip side="bottom" label="Status">
                    <strong>In Progress</strong> — actively being worked on.<br/>
                    <strong>On Hold</strong> — paused (client, consent, funding).<br/>
                    <strong>Potential</strong> — opportunity not yet won.<br/>
                    <strong>Completed</strong> — finished; still counted in every total and chart, listed separately below.
                  </InfoTip>
                </span>
              </th>
              <th className="px-4 py-3">
                <span className="inline-flex items-center">
                  Contract
                  <InfoTip side="bottom" label="Contract status">
                    <strong>Yes</strong> — signed.<br/>
                    <strong>Tentative</strong> — likely but not yet signed.<br/>
                    <strong>No</strong> — speculative / paused.
                  </InfoTip>
                </span>
              </th>
              <th className="px-4 py-3">
                <span className="inline-flex items-center">
                  Currency
                  <InfoTip side="bottom" label="Currency">
                    Fees are always entered and stored in <strong>NZD</strong> — the small figure beneath each amount is a display conversion only, and firm-wide reporting stays in NZD.<br/><br/>
                    <strong>Forecast months</strong> convert at the live European Central Bank rate (published once per working day — the "as at" date is always shown). <strong>Months already gone, and every invoiced figure,</strong> stay at the rate the project locked, so nothing already reported can move.
                  </InfoTip>
                </span>
              </th>

              <th className="px-4 py-3 text-right">
                <span className="inline-flex items-center">
                  Raw value
                  <InfoTip side="left" label="Raw value">
                    Sum of every scope’s monthly fees at face value — no probability applied.
                  </InfoTip>
                </span>
              </th>
              <th className="px-4 py-3 text-right">
                <span className="inline-flex items-center">
                  Weighted
                  <InfoTip side="left" label="Weighted value">
                    Each scope’s fees multiplied by its probability, then summed. A more cautious view of likely revenue.
                  </InfoTip>
                </span>
              </th>
              <th className="px-4 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {liveProjects.map((p) => renderRow(p, projects.indexOf(p)))}
            {liveProjects.length === 0 && completedProjects.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center eyebrow text-[var(--soft-fg)]">No projects yet. Click Add Project to start.</td></tr>
            )}
            {completedProjects.length > 0 && (
              <tr className="border-t-2 border-[var(--ink)] bg-[var(--paper-soft)]">
                <td colSpan={9} className="px-4 py-0">
                  <button
                    onClick={() => setShowCompleted((v) => !v)}
                    className="w-full flex items-center gap-2 py-3 text-left eyebrow hover:text-[var(--ink)] text-[var(--muted-fg)]"
                    data-testid="toggle-completed"
                  >
                    {showCompleted ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    Completed · {completedProjects.length}
                    <span className="normal-case tracking-[0.06em] font-mono-num text-[10px] text-[var(--soft-fg)]">
                      still counted in every total, chart and history — {fmtMoney(completedProjects.reduce((t, p) => t + projectRawTotal(p), 0))} raw value
                    </span>
                  </button>
                </td>
              </tr>
            )}
            {showCompleted && completedProjects.map((p) => renderRow(p, projects.indexOf(p), true))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Firm-wide projects grouped by owning principal — the leave_editor's
// landing view. Read-only list here; clicking a row opens the standard
// project form (fully editable). Reuses the visual pattern from
// Principals · "By Principal" but strips the summary/CSV admin-only chrome.
function ByPrincipalPortfolio({ data, rawData, base }) {
  const principals = rawData?.principals || [];
  const projects = data?.projects || [];
  const grouped = principals.map((pr) => ({
    ...pr,
    projects: (projects.filter((p) => p.principalId === pr.id && !isCompleted(p))),
    completed: (projects.filter((p) => p.principalId === pr.id && isCompleted(p))),
  }));
  // Any projects with an unrecognised (or missing) principalId still need
  // to show up — bucket them under a synthetic "unassigned" section.
  const known = new Set(principals.map((p) => p.id));
  const orphans = projects.filter((p) => !p.principalId || !known.has(p.principalId));
  if (orphans.length) grouped.push({ id: "__unassigned__", name: "Unassigned", projects: orphans.filter((p) => !isCompleted(p)), completed: orphans.filter(isCompleted) });

  return (
    <div className="space-y-8" data-testid="leave-editor-portfolio">
      <PageHeader
        section="FIRM-WIDE PROJECTS"
        title="All projects · by principal"
        subtitle={`${projects.length} project${projects.length === 1 ? "" : "s"} across ${grouped.filter((g) => g.projects.length).length} principals — click any row to open the project form.`}
      />
      {grouped.map((pr) => (
        <section key={pr.id} className="border border-[var(--rule)]" data-testid={`by-principal-${pr.id}`}>
          <div className="p-5 border-b border-[var(--rule)] flex items-baseline justify-between">
            <div>
              <div className="eyebrow">Principal</div>
              <h3 className="font-display text-lg mt-1">{pr.name}</h3>
            </div>
            <div className="font-mono-num text-xs text-[var(--soft-fg)]">
              {pr.projects.length} live{pr.completed?.length ? ` · ${pr.completed.length} completed` : ""}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="eyebrow text-left">
                <th className="px-4 py-2.5">Project</th>
                <th className="px-4 py-2.5">Contract</th>
                <th className="px-4 py-2.5 text-right">Raw value</th>
                <th className="px-4 py-2.5 text-right">Weighted</th>
                <th className="px-4 py-2.5 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {pr.projects.map((p) => {
                const chip = p.contract === "Yes" ? "chip-yes" : p.contract === "Tentative" ? "chip-tentative" : "chip-no";
                return (
                  <tr key={p.id} className="border-t border-[var(--rule)] hover:bg-[var(--paper-soft)]" data-testid={`by-principal-row-${p.id}`}>
                    <td className="px-4 py-2.5">
                      <div className="text-[var(--ink)]">{p.name}</div>
                      <div className="font-mono-num text-[10px] text-[var(--soft-fg)]">{projectNumber(p)} {p.client ? `· ${p.client}` : ""}</div>
                    </td>
                    <td className="px-4 py-2.5"><span className={`chip ${chip}`}>{contractLabel(p.contract)}</span></td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{fmtMoney(projectRawTotal(p))}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num text-[var(--soft-fg)]">{fmtMoney(projectWeightedTotal(p))}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Link
                        to={`${base}/edit/${encodeURIComponent(p.id)}`}
                        className="inline-flex items-center justify-center h-11 w-11 text-[var(--soft-fg)] hover:text-[var(--ink)] hover:bg-[var(--paper-soft)] transition-colors"
                        title="Open / edit project"
                        aria-label={`Edit project ${p.name}`}
                        data-testid={`by-principal-edit-${p.id}`}
                      >
                      <Pencil size={15} />
                      </Link>
                    </td>
                  </tr>
                );
              })}
              {pr.projects.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-6 text-center eyebrow text-[var(--soft-fg)]">No live projects under this principal.</td></tr>
              )}
              {(pr.completed || []).length > 0 && (
                <>
                  <tr className="border-t-2 border-[var(--ink)] bg-[var(--paper-soft)]">
                    <td colSpan={5} className="px-4 py-2 eyebrow text-[var(--muted-fg)]" data-testid={`by-principal-completed-${pr.id}`}>
                      Completed · {pr.completed.length} — still counted in every total and chart
                    </td>
                  </tr>
                  {pr.completed.map((p) => {
                    const chip = p.contract === "Yes" ? "chip-yes" : p.contract === "Tentative" ? "chip-tentative" : "chip-no";
                    return (
                      <tr key={p.id} className="border-t border-[var(--rule)] bg-[var(--paper-soft)]/40 text-[var(--muted-fg)] hover:bg-[var(--paper-soft)]" data-testid={`by-principal-row-${p.id}`}>
                        <td className="px-4 py-2.5">
                          <div>{p.name}</div>
                          <div className="font-mono-num text-[10px] text-[var(--soft-fg)]">{projectNumber(p)} {p.client ? `· ${p.client}` : ""}</div>
                        </td>
                        <td className="px-4 py-2.5"><span className={`chip ${chip}`}>{contractLabel(p.contract)}</span></td>
                        <td className="px-4 py-2.5 text-right font-mono-num">{fmtMoney(projectRawTotal(p))}</td>
                        <td className="px-4 py-2.5 text-right font-mono-num text-[var(--soft-fg)]">{fmtMoney(projectWeightedTotal(p))}</td>
                        <td className="px-4 py-2.5 text-right">
                          <Link
                            to={`${base}/edit/${encodeURIComponent(p.id)}`}
                            className="inline-flex items-center justify-center h-11 w-11 text-[var(--soft-fg)] hover:text-[var(--ink)] hover:bg-[var(--paper-soft)] transition-colors"
                            title="Open / edit project"
                            aria-label={`Edit project ${p.name}`}
                            data-testid={`by-principal-edit-${p.id}`}
                          >
                            <Pencil size={15} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </>
              )}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

